import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  useScrollTrigger,
  Slide,
  Container,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { BRAND } from '@bomatrust/shared';
import MobileMenu from './MobileMenu';

const navItems = [
  { label: 'How It Works', path: '/how-it-works' },
  { label: 'Properties', path: '/properties' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

/**
 * Header - Responsive navigation header
 */
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const scrollTrigger = useScrollTrigger({ disableHysteresis: true, threshold: 50 });

  const isActive = path => location.pathname === path;

  return (
    <>
      <Slide appear={false} direction="down" in={!scrollTrigger}>
        <AppBar
          position="fixed"
          elevation={scrollTrigger ? 1 : 0}
          component="header"
          role="banner"
          sx={{
            bgcolor: scrollTrigger ? 'background.paper' : 'transparent',
            borderBottom: scrollTrigger ? '1px solid' : 'none',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ py: 1 }}>
              {/* Logo */}
              <Link to="/" style={{ textDecoration: 'none' }} aria-label={`${BRAND.name} Home`}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mr: 4,
                  }}
                >
                  {BRAND.name}
                </Typography>
              </Link>

              {/* Desktop Navigation */}
              <Box
                component="nav"
                aria-label="Main navigation"
                sx={{
                  flexGrow: 1,
                  display: { xs: 'none', md: 'flex' },
                  gap: 1,
                }}
              >
                {navItems.map(item => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    sx={{
                      color: isActive(item.path) ? 'primary.main' : 'text.primary',
                      fontWeight: isActive(item.path) ? 600 : 400,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              {/* Auth Buttons - Desktop */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                <Button component={Link} to="/login" variant="outlined" color="primary">
                  Log In
                </Button>
                <Button component={Link} to="/signup" variant="contained" color="secondary">
                  Get Started
                </Button>
              </Box>

              {/* Mobile Menu Button */}
              <IconButton
                edge="end"
                color="primary"
                aria-label="Open navigation menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  ml: 'auto',
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>

      {/* Mobile Menu Drawer */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navItems={navItems}
      />
    </>
  );
};

export default Header;
